import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import type { AccessCardFindInterface } from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-find.dto';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import { ProfileFindDto, type ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import { RoleFindDto, type RoleFindInterface } from '@zaoblako/shared/modules/security/dtos/role/role-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { WorkScheduleFindDto, type WorkScheduleFindInterface } from '@zaoblako/shared/modules/security/dtos/work-schedule/work-schedule-find.dto';
import { SecurityDtoKeysEnum } from '@zaoblako/shared/modules/security/enums/security-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';

import * as yup from 'yup';

/** User response body */
export interface UserFindInterface {
  /** Unique identifier */
  id: number;
  /** User job */
  job: string;
  /** User full name (shortcut for `.profile.name` property, see {@link ProfileFindInterface.name}) */
  name?: string;
  /** Is user banned in tenant */
  banned: boolean;
  /** User soft deletion date and time */
  deleted: Date;
  /** Assigned roles */
  roles: RoleFindInterface[];
  /** Work schedule */
  workSchedule?: WorkScheduleFindInterface;
  /** Counterparty for salary distribution */
  contragent?: ContragentFindInterface;
  /** User profile */
  profile?: ProfileFindInterface;
  /** User who create this user */
  createdBy: UserFindInterface;
  /** List of groups where this user is supervisor */
  supervisorOf?: Omit<UserGroupFindInterface, 'users'>[];
  /** Access cards of PACS module */
  accessCard?: AccessCardFindInterface[];
  /** Linked Tenant */
  tenant?: TenantFindInterface;
  /** Approval scheme for BRS tasks */
  approvalScheme?: ApprovalSchemeFindInterface;
  /** Number of activities to delefate */
  activitiesCount?: number;
}

/** User response body DTO schema */
export const UserFindDto = SerdeDtoClass.create<UserFindInterface>(SecurityDtoKeysEnum.USER_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  job: {
    sortable: true,
    label: 'Должность',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'ФИО',
    serializer: yup.string(),
  },
  banned: {
    label: 'Заблокирован',
    serializer: yup.boolean(),
  },
  deleted: {
    label: 'Удален',
    serializer: yup.date(),
  },
  roles: {
    label: 'Роли',
    nested: RoleFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  workSchedule: {
    sortable: true,
    label: 'График',
    nested: WorkScheduleFindDto,
    serializer: (_, nested) => nested,
  },
  contragent: {
    sortable: true,
    optional: true,
    label: 'Контрагент',
    nested: ClientDtoKeysEnum.CONTRAGENT_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  profile: {
    sortable: true,
    label: 'Профиль',
    nested: ProfileFindDto,
    serializer: (_, nested) => nested,
  },
  createdBy: {
    label: 'Добавлен',
    nested: SecurityDtoKeysEnum.USER_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  supervisorOf: {
    label: 'Руководитель',
    nested: SecurityDtoKeysEnum.USER_GROUP_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  accessCard: {
    optional: true,
    label: 'Карты доступа',
    nested: PacsDtoKeysEnum.ACCESS_CARD_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  approvalScheme: {
    optional: true,
    label: 'Схема согласования',
    nested: CatalogDtosKeysEnum.APPROVAL_SCHEME_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  activitiesCount: {
    sortable: true,
    optional: true,
    label: 'Количество активных делегируемых дел',
    serializer: yup.number(),
  },
});
